// Jednotný profil hráče — /players/{fpl_id | player_key}. Fáze 1.
import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Trash, Timer, Article } from "@phosphor-icons/react";
import AppShell from "../components/AppShell";
import { useAuth } from "../contexts/AuthContext";
import { playerProfile, addToPool, patchPoolEntry, removePoolEntry, POOL_STATUSES } from "../lib/lab-api";
import { DnaPoolButton } from "../components/DnaPoolButton";
import AddPlayerMenu from "../components/AddPlayerMenu";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUS_LABEL = { a: null, d: "NEJISTÝ", i: "ZRANĚNÝ", s: "SUSPENDOVÁN", u: "PRYČ", n: "PRYČ" };

export default function PlayerProfile() {
    const { key } = useParams();
    const { user, login } = useAuth();
    const [p, setP] = useState(null);
    const [err, setErr] = useState(null);
    const [detail, setDetail] = useState(null);
    const [fdr, setFdr] = useState(null);
    const [pred, setPred] = useState(null);

    const load = useCallback(() => {
        playerProfile(key).then(setP).catch((e) => setErr(e.response?.data?.detail || "Hráč nenalezen"));
    }, [key]);
    useEffect(() => { setP(null); setErr(null); setDetail(null); setFdr(null); setPred(null); load(); }, [load]);

    useEffect(() => {
        const pk = p?.player_key;
        if (!pk) return;
        axios.get(`${API}/timing/player/${encodeURIComponent(pk)}`).then((r) => setDetail(r.data)).catch(() => {});
        axios.get(`${API}/timing/fdr-perf/${encodeURIComponent(pk)}`).then((r) => setFdr(r.data)).catch(() => {});
    }, [p?.player_key]);

    useEffect(() => {
        const fid = p?.fpl_id;
        if (!fid) return;
        axios.get(`${API}/predictions/v2/player/${fid}`, { timeout: 60000 }).then((r) => setPred(r.data)).catch(() => {});
    }, [p?.fpl_id]);

    const poolAdd = async () => {
        if (!user) { toast("Pro pool se přihlas", { action: { label: "Přihlásit", onClick: login } }); return; }
        try { await addToPool({ fpl_id: p.fpl_id, status: "watch" }); toast.success("Přidán do poolu"); load(); }
        catch (e) { toast.error(e.response?.data?.detail || "Přidání selhalo"); }
    };
    const poolStatus = async (status) => {
        try { await patchPoolEntry(p.pool_entry.id, { status }); load(); }
        catch { toast.error("Změna selhala"); }
    };
    const poolRemove = async () => {
        try { await removePoolEntry(p.pool_entry.id); toast.success("Odebrán z poolu"); load(); }
        catch { toast.error("Odebrání selhalo"); }
    };

    if (err) return (
        <AppShell><div className="max-w-3xl mx-auto border border-white/10 bg-black/40 p-8 font-mono text-sm text-white/60 text-center" data-testid="profile-error">{err}</div></AppShell>
    );
    if (!p) return <AppShell><div className="font-mono text-[11px] text-white/40 text-center py-20">Načítám profil…</div></AppShell>;

    const seasons = detail ? Object.entries(detail.seasons || {}).sort((a, b) => b[0].localeCompare(a[0])) : [];
    const agg = detail?.aggregate;
    const buckets = fdr?.buckets || fdr?.fdr_buckets || null;

    return (
        <AppShell>
            <div data-testid="player-profile-page" className="max-w-5xl mx-auto space-y-8">
                {/* Header */}
                <header className="flex flex-wrap items-start justify-between gap-4 border border-white/10 bg-black/40 p-5">
                    <div>
                        <h1 className="font-display font-black uppercase tracking-tight text-2xl md:text-3xl" data-testid="profile-name">
                            {p.full_name || p.name}
                            {STATUS_LABEL[p.status] && (
                                <span className="ml-3 font-mono text-[10px] px-2 py-1 bg-red-500/20 text-red-300 align-middle">{STATUS_LABEL[p.status]}</span>
                            )}
                        </h1>
                        <div className="font-mono text-[11px] uppercase text-white/55 mt-1.5" data-testid="profile-meta">
                            {p.team || "—"} · {p.position || "—"}{p.price != null ? ` · £${p.price}` : ""}
                            {p.ownership_pct != null ? ` · ${p.ownership_pct}% own.` : ""}
                        </div>
                        {p.news && <div className="font-mono text-[10px] text-orange-300/90 mt-1.5">⚠ {p.news}</div>}
                    </div>
                    <div className="flex flex-col gap-2 items-end" data-testid="profile-pool-actions">
                        {!p.pool_entry ? (
                            <button type="button" onClick={poolAdd} data-testid="profile-add-pool"
                                    disabled={p.fpl_id == null}
                                    className="font-mono text-[10px] uppercase tracking-widest px-4 py-2 bg-fa-mint text-black font-bold flex items-center gap-1.5 disabled:opacity-40">
                                <Plus size={12} weight="bold" /> Přidat do poolu
                            </button>
                        ) : (
                            <div className="flex items-center gap-2">
                                <select value={p.pool_entry.status} onChange={(e) => poolStatus(e.target.value)}
                                        data-testid="profile-pool-status"
                                        className="bg-black/60 border border-fa-mint/40 font-mono text-[10px] uppercase px-2 py-1.5 text-fa-mint focus:outline-none">
                                    {POOL_STATUSES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
                                </select>
                                <button type="button" onClick={poolRemove} title="Odebrat z poolu" data-testid="profile-remove-pool"
                                        className="p-1.5 border border-white/15 text-white/40 hover:text-red-400">
                                    <Trash size={12} />
                                </button>
                            </div>
                        )}
                        <AddPlayerMenu player={p} />
                        {p.player_key && (
                            <Link to={`/players?player=${encodeURIComponent(p.player_key)}`}
                                  data-testid="profile-open-explorer"
                                  className="font-mono text-[9px] uppercase tracking-widest text-white/40 hover:text-fa-mint flex items-center gap-1">
                                <Timer size={10} /> Plná analýza v Průzkumníku →
                            </Link>
                        )}
                    </div>
                </header>

                {/* Predikce v2 */}
                {pred && (pred.next_gw || pred.block4) && (
                    <div className="border border-fa-mint/30 bg-fa-mint/5 p-4" data-testid="profile-prediction">
                        <div className="flex flex-wrap items-center gap-5">
                            <div className="font-mono text-[9px] uppercase tracking-widest text-fa-mint">Prediction Model v2 · GW {pred.gw_from}</div>
                            {pred.next_gw && (
                                <div className="font-mono text-[11px] text-white/75">
                                    Příští GW: <span className="font-display font-black text-lg text-fa-mint tabular-nums">{pred.next_gw.predicted_total}</span> b
                                    <span className="text-white/40 ml-1">[{pred.next_gw.floor}–{pred.next_gw.ceiling}]</span>
                                </div>
                            )}
                            {pred.block4 && (
                                <div className="font-mono text-[11px] text-white/75">
                                    Blok 4 GW: <span className="font-display font-black text-lg text-fa-sky tabular-nums">{pred.block4.predicted_total}</span> b
                                    <span className="text-white/40 ml-1">({pred.block4.predicted_per_gw}/GW)</span>
                                </div>
                            )}
                            {(pred.next_gw || pred.block4) && (
                                <span className="font-mono text-[9px] text-white/50">jistota {{ HIGH: "vysoká", MEDIUM: "střední", LOW: "nízká" }[(pred.next_gw || pred.block4).confidence]} · minuty {Math.round((pred.next_gw || pred.block4).minutes_prob * 100)} %</span>
                            )}
                            {(pred.next_gw || pred.block4)?.preseason_share != null && (
                                <span className="font-mono text-[9px] text-fa-sky" data-testid="profile-preseason">příprava {(pred.next_gw || pred.block4).preseason_share} % minut (#{(pred.next_gw || pred.block4).preseason_rank} v týmu)</span>
                            )}
                            {(pred.next_gw || pred.block4)?.new_coach && (
                                <span className="font-mono text-[9px] uppercase text-[#FF8800] border border-[#FF8800]/50 px-1.5 py-0.5" data-testid="profile-new-coach" title="Hráč hraje pod novým trenérem — jiný herní styl, riziko výběru, jistota predikce snížena">⚠ Nový trenér</span>
                            )}
                            <Link to="/predictions" className="font-mono text-[9px] uppercase tracking-widest text-white/40 hover:text-fa-mint ml-auto">Celý model →</Link>
                        </div>
                    </div>
                )}

                {p.preseason && (
                    <div className="border border-fa-sky/30 bg-fa-sky/5 p-4" data-testid="profile-preseason-block">
                        <div className="flex flex-wrap items-center gap-5">
                            <div className="font-mono text-[9px] uppercase tracking-widest text-fa-sky">Letní příprava · {p.preseason.fpl_team}</div>
                            <div className="font-mono text-[11px] text-white/75">
                                Minuty: <span className="font-display font-black text-lg text-fa-sky tabular-nums">{p.preseason.minutes}</span>
                                <span className="text-white/40 ml-1">v {p.preseason.matches} zápasech</span>
                            </div>
                            <div className="font-mono text-[11px] text-white/75">
                                Starty: <span className="font-bold tabular-nums">{p.preseason.starts}/{p.preseason.matches}</span>
                            </div>
                            <div className="font-mono text-[11px] text-white/75">
                                Podíl minut: <span className="font-bold text-fa-sky tabular-nums">{p.preseason.share_pct} %</span>
                                <span className="text-white/40 ml-1">(#{p.preseason.rank} v týmu)</span>
                            </div>
                            <Link to="/preseason" className="font-mono text-[9px] uppercase tracking-widest text-white/40 hover:text-fa-sky ml-auto">Celá příprava →</Link>
                        </div>
                    </div>
                )}

                {p.gs_scouting && (() => {
                    const gp = p.gs_scouting.profile;
                    const lp = p.gs_scouting.latest_perf;
                    const b = p.gs_scouting.trend_bucket;
                    const trendTxt = b > 0 ? "Rising ↑" : b < 0 ? "Falling ↓" : "Watch →";
                    const trendCls = b > 0 ? "text-fa-mint" : b < 0 ? "text-red-300" : "text-white/60";
                    const setPieces = [gp?.penalties && `Penalty: ${gp.penalties}`,
                                       gp?.corners && `Rohy: ${gp.corners}`,
                                       gp?.direct_free_kicks && `Přímé kopy: ${gp.direct_free_kicks}`].filter(Boolean);
                    return (
                        <div className="border border-fa-violet/40 bg-fa-violet/5 p-4 space-y-2" data-testid="profile-gs-scouting">
                            <div className="flex flex-wrap items-center gap-4">
                                <div className="font-mono text-[9px] uppercase tracking-widest text-fa-cream">Redakční scouting · preseason</div>
                                <span className={`font-mono text-[10px] font-bold ${trendCls}`} data-testid="profile-gs-trend">{trendTxt}</span>
                                {gp?.preseason_rating != null && (
                                    <div className="font-mono text-[11px] text-white/75" data-testid="profile-gs-rating">
                                        Preseason rating: <span className="font-display font-black text-lg text-fa-cream tabular-nums">{gp.preseason_rating}</span><span className="text-white/40">/100</span>
                                    </div>
                                )}
                                {gp?.gw1_start_probability != null && (
                                    <div className="font-mono text-[11px] text-white/75" data-testid="profile-gs-gw1">
                                        Start GW1: <span className="font-bold text-fa-mint tabular-nums">{gp.gw1_start_probability} %</span>
                                    </div>
                                )}
                                {gp?.rotation_risk && <span className="font-mono text-[10px] text-white/60">Rotace: {gp.rotation_risk}</span>}
                                {gp?.tactical_role && <span className="font-mono text-[10px] text-white/60">Role: {gp.tactical_role}</span>}
                                <Link to="/preseason/database" className="font-mono text-[9px] uppercase tracking-widest text-white/40 hover:text-fa-cream ml-auto">Databáze →</Link>
                            </div>
                            {setPieces.length > 0 && (
                                <p className="font-mono text-[10px] text-fa-yellow/90" data-testid="profile-gs-setpieces">⚽ Standardky — {setPieces.join(" · ")}</p>
                            )}
                            {gp?.next_watchpoint && <p className="font-mono text-[10px] text-white/60">Sledovat příště: {gp.next_watchpoint}</p>}
                            {lp && (
                                <p className="font-mono text-[10px] text-white/60" data-testid="profile-gs-lastperf">
                                    Poslední přátelák {lp.date}{lp.minutes != null ? ` · ${lp.minutes} min` : ""}{lp.started === true ? " · start" : ""}
                                    {lp.notes ? ` — ${lp.notes}` : ""}
                                </p>
                            )}
                        </div>
                    );
                })()}

                {/* Aggregate pills */}
                {agg && (
                    <div className="flex flex-wrap gap-2" data-testid="profile-agg">
                        {[["Body celkem", agg.total_points], ["Zápasy", agg.games_played],
                          ["Konzistence", agg.consistency?.toFixed?.(1)], ["Explozivita", agg.explosiveness?.toFixed?.(1)],
                          ["Value", agg.value?.toFixed?.(1)]].map(([l, v]) => v != null && (
                            <div key={l} className="border border-white/10 bg-black/40 px-3 py-2">
                                <div className="font-mono text-[8px] uppercase text-white/40">{l}</div>
                                <div className="font-display font-bold text-lg tabular-nums">{v}</div>
                            </div>
                        ))}
                        {p.defcon?.prob_pct != null && (
                            <div className="border border-[#FF8800]/40 bg-[#FF8800]/5 px-3 py-2" data-testid="profile-defcon"
                                 title={`Odhad šance na 2 body za defenzivní akce (DC/90 ${p.defcon.per90} v 25/26, práh ${p.defcon.threshold})`}>
                                <div className="font-mono text-[8px] uppercase" style={{ color: "#FF8800" }}>DefCon šance</div>
                                <div className="font-display font-bold text-lg tabular-nums" style={{ color: "#FF8800" }}>{p.defcon.prob_pct}%</div>
                            </div>
                        )}
                    </div>
                )}

                {/* Seasons */}
                {seasons.length > 0 && (
                    <section data-testid="profile-seasons">
                        <h2 className="font-display font-black uppercase text-base mb-3">Historie sezón</h2>
                        <div className="border border-white/10 bg-[#0d0d0d] overflow-x-auto">
                            <table className="w-full text-left">
                                <thead>
                                    <tr className="border-b border-white/10 font-mono text-[9px] uppercase tracking-widest text-white/40">
                                        <th className="px-3 py-2">Sezóna</th><th className="px-3 py-2">Tým</th>
                                        <th className="px-3 py-2 text-right">Body</th><th className="px-3 py-2 text-right">Zápasy</th>
                                        <th className="px-3 py-2 text-right">PPG</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {seasons.map(([season, s]) => (
                                        <tr key={season} className="border-b border-white/5 font-mono text-[11px]">
                                            <td className="px-3 py-2 text-white/80">{season}</td>
                                            <td className="px-3 py-2 text-white/50">{detail.meta?.teams_by_season?.[season] || "—"}</td>
                                            <td className="px-3 py-2 text-right tabular-nums font-bold text-fa-mint">{s.total_points}</td>
                                            <td className="px-3 py-2 text-right tabular-nums text-white/60">{s.games_played}</td>
                                            <td className="px-3 py-2 text-right tabular-nums">{s.games_played ? (s.total_points / s.games_played).toFixed(2) : "—"}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </section>
                )}

                {/* FDR performance */}
                {buckets && (
                    <section data-testid="profile-fdr">
                        <h2 className="font-display font-black uppercase text-base mb-3">Výkon dle síly soupeře (FDR)</h2>
                        <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                            {[1, 2, 3, 4, 5].map((lvl) => {
                                const b = buckets[String(lvl)] || buckets[lvl];
                                return (
                                    <div key={lvl} className="border border-white/10 bg-black/40 p-3" data-testid={`profile-fdr-${lvl}`}>
                                        <div className="font-mono text-[8px] uppercase text-white/40">FDR {lvl}</div>
                                        {b && b.games ? (
                                            <>
                                                <div className="font-display font-bold text-xl tabular-nums">{(b.ppg ?? (b.pts / b.games)).toFixed(2)}</div>
                                                <div className="font-mono text-[8px] text-white/45">{b.games} zápasů · haul {b.haul_pct ?? b.hauls}%</div>
                                            </>
                                        ) : (
                                            <div className="font-mono text-[9px] text-white/30 py-1.5">bez zápasů</div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </section>
                )}

                {/* Articles */}
                {p.articles?.length > 0 && (
                    <section data-testid="profile-articles">
                        <h2 className="font-display font-black uppercase text-base mb-3 flex items-center gap-2">
                            <Article size={16} className="text-fa-cream" /> Související články
                        </h2>
                        <div className="grid md:grid-cols-2 gap-3">
                            {p.articles.map((a) => (
                                <Link key={a.slug} to={`/academy/${a.slug}`}
                                      className="border border-white/10 bg-black/40 p-4 hover:border-fa-cream/40 transition-colors group"
                                      data-testid={`profile-article-${a.slug}`}>
                                    <div className="font-display font-bold text-sm group-hover:text-fa-cream transition-colors">{a.title}</div>
                                    {a.perex && <div className="font-mono text-[10px] text-white/50 mt-1 line-clamp-2">{a.perex}</div>}
                                </Link>
                            ))}
                        </div>
                    </section>
                )}

                {!p.player_key && (
                    <div className="border border-white/10 bg-black/40 p-4 font-mono text-[10px] text-white/45" data-testid="profile-no-history">
                        Hráč nemá napárovanou historickou FPL stopu — dostupná jsou jen aktuální data z bootstrapu.
                    </div>
                )}
            </div>
        </AppShell>
    );
}
